import { Component } from 'react'
import { OperationStart, ShippingStartListAndInput } from 'stylewhere/components'
import { Shippings, ShippingParcel, OperationConfigurations, ShippingSelectData, Asn } from 'stylewhere/api'
import { ShippingExtensions } from 'stylewhere/extensions'
import {
  ShippingOperationConfig,
  RemoteOperation,
  Router,
  getDataFromSchema,
  Sounds,
  FormSchemaData,
  FormSchema,
  AppStore
} from 'stylewhere/shared'
import { PackingOperationConfig, RawOperationConfig } from 'stylewhere/shared/RemoteOperation'
import type { Routes } from 'stylewhere/pages'
import { CustomError } from 'stylewhere/shared/errors'
import {
  isModalError,
  showToastError,
  askUserConfirmation,
  canDeleteParcel,
  getDefaultFilterShipment
} from 'stylewhere/utils'
import { __, T } from 'stylewhere/i18n'
import { ShippingParcelStartParams } from '../ShippingParcelStart'

export interface ShippingStartParams {
  opCode: string
}

interface LocationState {
  filterParams?: Record<string, any>
}

interface ShippingStartProps {
  match: { params: ShippingStartParams }
  location: { state?: LocationState }
}

export interface WithEncodingStartState {
  renderIteration: number
  loading: boolean
}

export default class WithEncodingStart extends Component<ShippingStartProps, WithEncodingStartState> {
  submitPath: Routes = '/shipping/withEncoding/:opCode/reading'
  asnParcelPath: Routes = '/shipping/:opCode/asn/:asnId'
  isModal = false

  state: WithEncodingStartState = {
    renderIteration: 0,
    loading: false
  }

  get locationState(): LocationState | undefined {
    return this.props.location.state
  }

  get operation() {
    return RemoteOperation.getOperationConfig<ShippingOperationConfig | PackingOperationConfig>(
      this.props.match.params.opCode
    )
  }

  componentDidMount() {
    this.isModal = isModalError(this.operation)
    this.checkEncodingOperation()
  }

  isParcelByParcel = () => {
    return this.operation.shippingMode === 'parcelByParcel'
  }

  checkEncodingOperation = async () => {
    // get related encoding operation
    const encodingOperationConfig = await OperationConfigurations.search<RawOperationConfig>({
      size: 1,
      equalCodes: this.operation.validateEncodingOperationCode
    })
    if (!encodingOperationConfig?.content || encodingOperationConfig.content.length === 0) {
      showToastError(new Error('Missing embedded encoding operation'), __(T.error.error), true)
      Router.navigate('/')
    }
  }

  onSubmit = async (formData, operation: ShippingOperationConfig, schema) => {
    if (schema.length === 0) {
      // auto submit
      Router.navigate(this.submitPath, { opCode: this.operation.code }, {})
      return
    }

    const payloadStartParcel: any = getDataFromSchema(formData, schema)
    try {
      const parcel: ShippingParcel = await Shippings.startParcel(
        this.operation,
        payloadStartParcel,
        this.operation.startParcelWithAsn
      )
      if (!formData.parcelCode && parcel?.code && schema.some((s) => s.name === 'parcelCode')) {
        formData.parcelCode = parcel.code
      }
      Router.navigate(
        this.submitPath,
        { opCode: operation.code },
        {
          state: {
            formData,
            parcel,
            filterParams: this.operation.shippingMode === 'parcelByParcel' ? { tab: 'parcel' } : {}
          }
        }
      )
    } catch (e) {
      if (e instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(e.message, payloadStartParcel)}. [${e.errorCode}]`,
          __(T.error.error),
          this.isModal,
          () => {}
        )
        return
      }
      showToastError(e, 'Start Parcel Error', this.isModal, () => {})
    }
  }

  onSelectParcel = async (selected: ShippingSelectData) => {
    const selectedParcel = selected as ShippingParcel
    if (['RECEIVED', 'READY_TO_RECEIVE', 'FORCE_RECEIVED', 'READY_TO_SEND', 'PACKED'].includes(selectedParcel.status)) {
      Router.navigate(
        '/shipping/:opCode/parcel/:parcelId',
        {
          opCode: this.operation.code,
          parcelId: selectedParcel.id
        },
        { state: { filterParams: Router.getQueryParams() } }
      )
    } else {
      const schemaParcel = ShippingExtensions.formSchema(this.operation, undefined, ['', 'parcel'])
      const formattedData = await Shippings.getInitialValueParcel(schemaParcel, selectedParcel.asn, selectedParcel)
      // override payload to send direct id for place if present
      const payloadStartParcel = formattedData.asn
        ? {
            ...getDataFromSchema(formattedData, schemaParcel),
            asn: {
              code: formattedData.asn.code,
              destinationPlaceId: formattedData.asn.destinationPlaceId?.id,
              originPlaceId: formattedData.asn.originPlaceId?.id
            }
          }
        : formattedData

      const operation = { ...this.operation }
      if (selectedParcel.status === 'INBOUND_ERROR') {
        operation.type = 'INBOUND'
      } else if (selectedParcel.status === 'OUTBOUND_ERROR') {
        operation.type = 'OUTBOUND'
      }

      try {
        const parcel = await Shippings.startParcel(operation, payloadStartParcel, operation.startParcelWithAsn)
        Router.navigate(
          this.submitPath,
          { opCode: operation.code },
          { state: { formData: formattedData, parcel, filterParams: Router.getQueryParams() } }
        )
      } catch (err) {
        showToastError(err, 'Start Parcel Error', this.isModal)
      }
    }
  }

  canDeleteOrClear = (data: ShippingSelectData, shipment: boolean) => {
    if (!data || !this.operation.canDeleteParcel) return false
    if (!shipment) {
      return canDeleteParcel(data as ShippingParcel, this.operation)
    }
    return ['IN_OUTBOUND', 'DRAFT', 'IN_INBOUND'].includes(data.status)
  }

  onSelectAsn = async (selected: ShippingSelectData) => {
    try {
      const asn = selected as Asn
      const directAsn = asn.status && Shippings.getStartAsnStatus(this.operation).includes(asn.status)
      if (!directAsn || this.operation.shippingMode === 'parcelByParcel') {
        this.navigateToAsn(asn)
      } else if (asn?.id) {
        const formData = {
          shipmentCode: asn.code,
          originPlaceId:
            asn.originPlaceId && asn.originPlaceId !== null ? asn.originPlaceId : AppStore.defaultWorkstation?.placeId,
          destinationPlaceId:
            asn.destinationPlaceId && asn.destinationPlaceId !== null
              ? asn.destinationPlaceId
              : AppStore.defaultWorkstation?.placeId,
          attributes: asn.attributes ?? {}
        }
        const newAsn = await Shippings.startAsn(this.operation, formData)
        this.navigateToAsn(newAsn)
      } else {
        throw new Error(__(T.error.submit_asn))
      }
    } catch (err) {
      showToastError(err, __(T.error.submit_asn), this.isModal)
    }
  }

  navigateToAsn = (asn: Asn, newAsn?: boolean) => {
    const nextRouteParams: ShippingParcelStartParams = {
      opCode: this.operation.code,
      asnId: asn.id
    }
    Router.navigate(this.asnParcelPath, nextRouteParams, {
      state: {
        asn,
        filterParams:
          this.operation.shippingMode === 'parcelByParcel'
            ? newAsn
              ? { tab: 'shipment' }
              : Router.getQueryParams()
            : {}
      }
    })
  }

  onCreateAsn = async (formData: FormSchemaData, operation: ShippingOperationConfig, schema: FormSchema) => {
    try {
      if (!formData) throw new Error(__(T.error.submit_asn))
      // si normalizzano i dati del form per utilizzare anche eventuali confirmValuePath presenti nel FormSchema
      const payloadStartAsn = getDataFromSchema(formData, schema)
      const newAsn = await Shippings.startAsn(operation, payloadStartAsn)
      const asnId = newAsn.id
      if (asnId) {
        this.navigateToAsn(newAsn, true)
      } else {
        throw new Error(__(T.error.submit_asn))
      }
    } catch (err) {
      showToastError(err, __(T.error.submit_asn), this.isModal)
    }
  }

  handleDelete = async (data: ShippingSelectData, shipment: boolean) => {
    const confirmed = await askUserConfirmation(
      shipment ? __(T.title.delete_shipment_title) : __(T.custom.deleteSession),
      shipment ? __(T.subtitle.delete_shipment_message) : __(T.custom.deleteSessionMessage)
    )
    if (confirmed) {
      const result = await Shippings._deleteEntity(data.id, shipment ? 'shipment' : 'parcel', this.operation)
      if (result.success) {
        Sounds.success()
        this.setState((prevState) => ({
          renderIteration: prevState.renderIteration + 1
        }))
      } else {
        showToastError(result.message, __(T.misc.error), this.isModal)
      }
    }
  }

  onFiltersChange = (filters: any) => {
    Router.setQueryParams(filters)
  }

  getFilterParams = () => {
    if (this.locationState?.filterParams) {
      return this.locationState?.filterParams
    }
    return Router.getQueryParams()
  }

  render() {
    const isParcel = this.isParcelByParcel()
    const { renderIteration } = this.state
    if (this.operation.showListOnStart) {
      const defaultFilter = getDefaultFilterShipment(this.operation, this.getFilterParams(), {})
      return (
        <ShippingStartListAndInput
          key={renderIteration}
          operation={this.operation}
          onSelect={(selected: ShippingSelectData, shipment: boolean) =>
            shipment ? this.onSelectAsn(selected) : this.onSelectParcel(selected)
          }
          onClickReadParcel={() => {}}
          onCreate={(
            formData: FormSchemaData,
            operation: ShippingOperationConfig,
            schema: FormSchema,
            shipment: boolean
          ) => (shipment ? this.onCreateAsn(formData, operation, schema) : this.onSubmit(formData, operation, schema))}
          shipment={!isParcel || defaultFilter.tab === 'shipment'}
          onDelete={this.handleDelete}
          canDelete={this.canDeleteOrClear}
          defaultFilter={defaultFilter}
          onFiltersChange={this.onFiltersChange}
        />
      )
    }
    return <OperationStart submitPath={this.submitPath} extensions={ShippingExtensions} onSubmit={this.onSubmit} />
  }
}
